/**
 * CreateActivityExtension
 *
 * Autodesk Forge Viewer Extension that adds a "Create Activity" button to the toolbar.
 * The button is enabled only after the model is fully loaded.
 */

export interface ICreateActivityExtensionOptions {
  onCreateActivity: () => void;
}

export class CreateActivityExtension extends Autodesk.Viewing.Extension {
  private button: Autodesk.Viewing.UI.Button | null = null;
  private onCreateActivity: () => void;
  private modelLoaded: boolean = false;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ICreateActivityExtensionOptions) {
    super(viewer, options);
    this.onCreateActivity = options.onCreateActivity;
  }

  load() {
    this.viewer.addEventListener(
      Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
      this.onGeometryLoaded
    );

    this.viewer.addEventListener(
      Autodesk.Viewing.MODEL_ADDED_EVENT,
      this.onModelAdded
    );

    // Delay ensures toolbar is ready
    setTimeout(() => {
      this.createToolbarButton();
    }, 100);

    return true;
  }

  unload() {
    this.viewer.removeEventListener(
      Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
      this.onGeometryLoaded
    );

    this.viewer.removeEventListener(
      Autodesk.Viewing.MODEL_ADDED_EVENT,
      this.onModelAdded
    );

    if (this.button) {
      const toolbar = this.viewer.getToolbar(true) as any;
      const settingsTools = toolbar?.getControl('settingsTools');
      if (settingsTools && settingsTools.removeControl) {
        settingsTools.removeControl(this.button.getId());
      }
      this.button = null;
    }

    return true;
  }

  private onGeometryLoaded = () => {
    this.modelLoaded = true;
    this.updateButtonState();
  };

  private onModelAdded = () => {
    this.modelLoaded = true;
    this.updateButtonState();
  };

  private createToolbarButton() {
    const toolbar = this.viewer.getToolbar(true) as any;

    if (!toolbar) {
      console.warn('Toolbar not available');
      return;
    }

    let settingsTools = toolbar.getControl('settingsTools');

    if (!settingsTools) {
      settingsTools = new Autodesk.Viewing.UI.ControlGroup('settingsTools');
      if (toolbar.addControl) {
        toolbar.addControl(settingsTools);
      }
    }

    this.button = new Autodesk.Viewing.UI.Button('create-activity-button');
    this.button.setToolTip('Criar Atividade');
    this.button.setIcon('adsk-icon-properties');

    // Check if models already loaded (handles case where button is created after models finish loading)
    const allModels = this.viewer.getAllModels ? this.viewer.getAllModels() : 
                      (this.viewer.model ? [this.viewer.model] : []);
    
    if (allModels && allModels.length > 0) {
      this.modelLoaded = true;
    }

    if (this.modelLoaded) {
      this.button.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
    } else {
      this.button.setState(Autodesk.Viewing.UI.Button.State.DISABLED);
    }

    this.button.onClick = () => {
      if (this.modelLoaded) {
        this.onCreateActivity();
      }
    };

    if (settingsTools && settingsTools.addControl) {
      settingsTools.addControl(this.button);
    }
  }

  private updateButtonState() {
    if (this.button) {
      if (this.modelLoaded) {
        this.button.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
      } else {
        this.button.setState(Autodesk.Viewing.UI.Button.State.DISABLED);
      }
    }
  }

  static register() {
    Autodesk.Viewing.theExtensionManager.registerExtension(
      'CreateActivityExtension',
      CreateActivityExtension as any
    );
  }
}
